import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const api = axios.create({
  baseURL: API_URL,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
};

export const gymAPI = {
  getAll: () => api.get('/gyms'),
  getById: (id) => api.get(`/gyms/${id}`),
  create: (data) => api.post('/gyms', data),
  update: (id, data) => api.put(`/gyms/${id}`, data),
  delete: (id) => api.delete(`/gyms/${id}`),
};

export const revenueAPI = {
  getAll: (gymId) => api.get('/revenues', { params: { gym_id: gymId } }),
  create: (data) => api.post('/revenues', data),
  update: (id, data) => api.put(`/revenues/${id}`, data),
  delete: (id) => api.delete(`/revenues/${id}`),
};

export const expenseAPI = {
  getAll: (gymId) => api.get('/expenses', { params: { gym_id: gymId } }),
  create: (data) => api.post('/expenses', data),
  update: (id, data) => api.put(`/expenses/${id}`, data),
  delete: (id) => api.delete(`/expenses/${id}`),
  getCategories: (gymId) => api.get('/expense-categories', { params: { gym_id: gymId } }),
};

export const billAPI = {
  getAll: (gymId) => api.get('/bills', { params: { gym_id: gymId } }),
  getConsolidado: (gymId) => api.get('/bills-consolidado', { params: { gym_id: gymId } }),
  create: (data) => api.post('/bills', data),
  update: (id, data) => api.put(`/bills/${id}`, data),
  delete: (id) => api.delete(`/bills/${id}`),
};

export const revenueSourceAPI = {
  getAll: (gymId) => api.get('/revenue-sources', { params: { gym_id: gymId } }),
  create: (data) => api.post('/revenue-sources', data),
  update: (id, data) => api.put(`/revenue-sources/${id}`, data),
  delete: (id) => api.delete(`/revenue-sources/${id}`),
  getTypes: (gymId) => api.get('/revenue-source-categories', { params: { gym_id: gymId } }),
  createType: (data) => api.post('/revenue-source-categories', data),
  updateType: (id, data) => api.put(`/revenue-source-categories/${id}`, data),
  deleteType: (id) => api.delete(`/revenue-source-categories/${id}`),
};

export const goalAPI = {
  getAll: (gymId) => api.get('/goals', { params: { gym_id: gymId } }),
  create: (data) => api.post('/goals', data),
  update: (id, data) => api.put(`/goals/${id}`, data),
  delete: (id) => api.delete(`/goals/${id}`),
  getCategories: (gymId) => api.get('/goal-categories', { params: { gym_id: gymId } }),
  createCategory: (data) => api.post('/goal-categories', data),
  updateCategory: (id, data) => api.put(`/goal-categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/goal-categories/${id}`),
};

export const dashboardAPI = {
  getOverview: (gymId, month, year, modo) => {
    const params = { gym_id: gymId };
    if (month) params.month = month;
    if (year) params.year = year;
    if (modo) params.modo = modo;
    return api.get('/dashboard/overview', { params });
  },
  getMonthlyTrend: (gymId, months = 6, modo) => api.get('/dashboard/monthly-trend', { params: { gym_id: gymId, months, ...(modo ? { modo } : {}) } }),
  getPeriodo: (gymId, mesInicio, anoInicio, mesFim, anoFim, modo) => api.get('/dashboard/periodo', {
    params: { gym_id: gymId, mes_inicio: mesInicio, ano_inicio: anoInicio, mes_fim: mesFim, ano_fim: anoFim, ...(modo ? { modo } : {}) }
  }),
};

export const consolidadoAPI = {
  getOverview: (gymIds, month, year, modo) => {
    const params = { gym_ids: (gymIds || []).join(',') };
    if (month) params.month = month;
    if (year) params.year = year;
    if (modo) params.modo = modo;
    return api.get('/consolidado/overview', { params });
  },
};

export const investmentAPI = {
  getAll: (gymId) => api.get('/investments', { params: { gym_id: gymId } }),
  getTotal: (gymId) => api.get('/investments/total', { params: { gym_id: gymId } }),
  create: (data) => api.post('/investments', data),
  update: (id, data) => api.put(`/investments/${id}`, data),
  delete: (id) => api.delete(`/investments/${id}`),
  getCategories: (gymId) => api.get('/investment-categories', { params: { gym_id: gymId } }),
  createCategory: (data) => api.post('/investment-categories', data),
  deleteCategory: (id) => api.delete(`/investment-categories/${id}`),
  getMonthlyROI: (gymId, year) => api.get('/roi/monthly', { params: { gym_id: gymId, year } }),
};

// ============ CONTAS BANCÁRIAS ============
export const contaBancariaAPI = {
  getAll: (gymId) => api.get('/contas-bancarias', { params: { gym_id: gymId } }),
  create: (data) => api.post('/contas-bancarias', data),
  update: (id, data) => api.put(`/contas-bancarias/${id}`, data),
  delete: (id) => api.delete(`/contas-bancarias/${id}`),
  recalcularSaldo: (id, data) => api.post(`/contas-bancarias/${id}/recalcular-saldo`, data),
};

// ============ MOVIMENTAÇÕES DE CAIXA ============
export const movimentacaoCaixaAPI = {
  getAll: (gymId, filters = {}) => api.get('/movimentacoes-caixa', { params: { gym_id: gymId, ...filters } }),
  create: (data) => api.post('/movimentacoes-caixa', data),
  update: (id, data) => api.put(`/movimentacoes-caixa/${id}`, data),
  delete: (id) => api.delete(`/movimentacoes-caixa/${id}`),
  getConsolidado: (gymId, mes, ano) => api.get('/caixa-consolidado', { params: { gym_id: gymId, mes, ano } }),
  getSemConta: (gymId, filters = {}) => api.get('/movimentacoes-sem-conta', { params: { gym_id: gymId, ...filters } }),
  vincularConta: (data) => api.post('/movimentacoes-caixa/vincular-conta', data),
  corrigirTransferencias: (data) => api.post('/movimentacoes-caixa/corrigir-transferencias', data),
};

// ============ IMPORTAÇÃO OFX ============
export const importacaoOFXAPI = {
  preview: (gymId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/importar-ofx/preview?gym_id=${gymId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  confirmar: (data) => api.post('/importar-ofx/confirmar', data),
  listar: (gymId) => api.get('/importacoes-ofx', { params: { gym_id: gymId } }),
  excluir: (loteId) => api.delete(`/importacoes-ofx/${loteId}`),
};

// ============ PROJEÇÃO V2 (integrada com Caixa) ============
export const projecaoAPI = {
  getProjecaoV2: (gymId, mesesFuturos = 12) => api.get('/projecao-caixa-v2', { params: { gym_id: gymId, meses_futuros: mesesFuturos } }),
};

// ============ REGRAS DE CATEGORIZAÇÃO OFX ============
export const regrasOFXAPI = {
  listar: (gymId) => api.get('/regras-ofx', { params: { gym_id: gymId } }),
  criar: (data) => api.post('/regras-ofx', data),
  atualizar: (id, data) => api.put(`/regras-ofx/${id}`, data),
  excluir: (id) => api.delete(`/regras-ofx/${id}`),
};

// ============ DRILL-DOWN (composição dos números) ============
export const drillDownAPI = {
  get: (gymId, tipo, mes, ano) => api.get('/drill-down', { params: { gym_id: gymId, tipo, mes, ano } }),
};

export default api;